import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Checkbox } from '../atoms/Checkbox';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { Select } from '../atoms/Select/Select';
import { NumericParameterInput } from '../atoms/NumericParameterInput/NumericParameterInput';
import { Text } from '../atoms/Typography/Text';
import { useTranslation } from '../../i18n/useTranslation';
import {
  BUDGET_CATEGORIES_FOR_SUGGESTIONS,
  LIVESTOCK_CATEGORIES_FOR_SUGGESTIONS,
  type CaptureSuggestionDto,
  type SuggestionKind,
  type SuggestionPayload,
  type SuggestionPayloadByKind,
} from '../../types/capture';
import {
  daysSince,
  formatDay,
  formatNumber,
  kindLabel,
  recurrenceSummary,
  suggestionText,
} from './suggestionFormat';
import './SuggestionRow.css';
import { parameterLabel } from '../measurements/measurementHistoryUtils';

export interface SuggestionRowProps {
  suggestion: CaptureSuggestionDto;
  /** The payload as currently edited (the suggested one until the user changes it). */
  payload: SuggestionPayload;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** An edit — the parent also checks the row. */
  onPayloadChange: (payload: SuggestionPayload) => void;
  /** Hide the kind badge (e.g. a list of tasks only). */
  showKind?: boolean;
  /** Compact card: editors only for rows that need checking. */
  compact?: boolean;
  /** The row (any of its controls) received focus — highlights its phrase in the transcript. */
  onFocusRow?: (suggestion: CaptureSuggestionDto) => void;
  /** Show the phrase the suggestion was taken from (typed entries). */
  showPhrase?: boolean;
  disabled?: boolean;
  /** The confirmed tank — for links into it (the quarantine hint of a new fish). */
  aquariumId?: string;
}

/** The "Please check" text of a reason; choosing an animal has its own wording. */
/** The dosing page opens the plan's editor with the new dose filled in (the dose guard runs there). */
export function dosingChangeHref(aquariumId: string, scheduleId: string, newAmount: number): string {
  const query = new URLSearchParams({ edit: scheduleId, dose: String(newAmount) });
  return `/aquariums/${encodeURIComponent(aquariumId)}/dosing?${query.toString()}`;
}

function reasonKey(kind: SuggestionKind, reason: string): string {
  if (kind === 'livestock_lost' && (reason === 'no_match' || reason === 'multiple_matches')) return `voice.suggestion.reason.animal_${reason}`;
  return `voice.suggestion.reason.${reason}`;
}

/** Parses a typed decimal (comma or point). Returns null for anything that is not a finite number. */
export function parseDecimal(raw: string): number | null {
  const normalised = raw.trim().replace(',', '.');
  if (normalised === '' || !/^-?\d*\.?\d+$/.test(normalised)) return null;
  const value = Number(normalised);
  return Number.isFinite(value) ? value : null;
}

function DecimalEditor({
  label,
  value,
  unit,
  error,
  onValue,
  onFocus,
  disabled,
}: {
  label: string;
  value: number | null;
  unit?: string;
  error?: string;
  onValue: (value: number) => void;
  onFocus?: () => void;
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [text, setText] = useState(value === null ? '' : formatNumber(value, i18n.language).replace(/\s/g, ''));
  const [invalid, setInvalid] = useState(false);

  // An external change (reset after a save, re-analysis) replaces the text.
  useEffect(() => {
    setText((current) => (parseDecimal(current) === value ? current : value === null ? '' : String(value)));
  }, [value]);

  return (
    <NumericParameterInput
      label={label}
      unit={unit}
      value={text}
      disabled={disabled}
      error={invalid ? t('voice.suggestion.invalidNumber') : error}
      onFocus={onFocus}
      onChange={(next) => {
        setText(next);
        const parsed = parseDecimal(next);
        setInvalid(next.trim() !== '' && parsed === null);
        if (parsed !== null) onValue(parsed);
      }}
    />
  );
}

/**
 * One suggestion as a selectable row (UC-A/UC-B review): a checkbox labelled
 * with the item text, the kind as a text badge, "Please check" with its reason
 * in text, and an inline editor for what is most often wrong (the value, the
 * task, the product, the date). Reused by the review card and Detected Tasks.
 */
export function SuggestionRow({
  suggestion,
  payload,
  checked,
  onCheckedChange,
  onPayloadChange,
  showKind = true,
  compact = false,
  onFocusRow,
  showPhrase = false,
  disabled = false,
  aquariumId,
}: SuggestionRowProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const reasonId = useId();
  const [editing, setEditing] = useState(false);
  const kind: SuggestionKind = suggestion.kind;
  const text = suggestionText(kind, payload as SuggestionPayloadByKind[typeof kind], t, language);
  const reasons = suggestion.needsReview ? suggestion.reviewReasons : [];
  const focus = (): void => onFocusRow?.(suggestion);
  const change = <K extends SuggestionKind>(next: SuggestionPayloadByKind[K]): void => onPayloadChange(next);
  const showEditors = !compact || suggestion.needsReview;

  const editToggle = (
    <Button
      variant="ghost"
      size="sm"
      className="suggestion-row__edit"
      aria-expanded={editing}
      aria-label={t('voice.suggestion.editLabel', { item: text })}
      onClick={() => setEditing((v) => !v)}
      onFocus={focus}
      disabled={disabled}
    >
      {t('common.edit')}
    </Button>
  );

  let details: ReactNode = null;
  switch (kind) {
    case 'measurement': {
      const p = payload as SuggestionPayloadByKind['measurement'];
      const last = p.lastValue;
      details = (
        <>
          {showEditors && (
            <DecimalEditor
              label={`${t('voice.suggestion.valueLabel')} (${parameterLabel(t, p.parameterId, p.parameterName)})`}
              value={p.value}
              unit={p.unit}
              disabled={disabled}
              error={suggestion.needsReview ? t('voice.suggestion.checkValue') : undefined}
              onFocus={focus}
              onValue={(value) => change<'measurement'>({ ...p, value })}
            />
          )}
          {last && (
            <Text variant="caption" color="muted">
              {daysSince(last.measuredAt) === 0
                ? t('voice.suggestion.lastValueToday', { value: formatNumber(last.value, language), unit: last.unit })
                : t('voice.suggestion.lastValue', {
                    value: formatNumber(last.value, language),
                    unit: last.unit,
                    count: daysSince(last.measuredAt),
                  })}
            </Text>
          )}
        </>
      );
      break;
    }
    case 'dosing': {
      const p = payload as SuggestionPayloadByKind['dosing'];
      const choose = p.scheduleId === null || suggestion.reviewReasons.includes('no_match');
      details = (
        <>
          {p.scheduleId !== null && !choose && (
            <Text variant="caption" color="muted">{t('voice.suggestion.fromPlan')}</Text>
          )}
          {showEditors && p.candidates.length > 0 && (choose || editing) && (
            <Select
              label={t('voice.suggestion.whichProduct')}
              value={p.scheduleId ?? ''}
              disabled={disabled}
              onFocus={focus}
              onChange={(e) => {
                const candidate = p.candidates.find((c) => c.scheduleId === e.target.value);
                if (candidate) change<'dosing'>({ ...p, scheduleId: candidate.scheduleId, productName: candidate.productName, unit: candidate.unit });
              }}
              options={[
                { value: '', label: t('voice.suggestion.chooseProduct'), disabled: true },
                ...p.candidates.map((c) => ({ value: c.scheduleId, label: `${c.productName} (${c.unit})` })),
              ]}
            />
          )}
          {showEditors && editing && (
            <DecimalEditor
              label={t('voice.suggestion.amount')}
              value={p.amount}
              unit={p.unit}
              disabled={disabled}
              onFocus={focus}
              onValue={(amount) => change<'dosing'>({ ...p, amount })}
            />
          )}
          {showEditors && editToggle}
        </>
      );
      break;
    }
    case 'dosing_change': {
      const p = payload as SuggestionPayloadByKind['dosing_change'];
      const choose = p.scheduleId === null || suggestion.reviewReasons.includes('no_match');
      details = (
        <>
          <Text variant="caption" color="muted">{t('voice.suggestion.dosingChangeHint')}</Text>
          {aquariumId && p.scheduleId !== null && (
            <Text variant="caption">
              <Link className="suggestion-row__link" to={dosingChangeHref(aquariumId, p.scheduleId, p.newAmount)} onFocus={focus}>
                {t('voice.suggestion.dosingChangeLink')}
              </Link>
            </Text>
          )}
          {showEditors && p.candidates.length > 0 && (choose || editing) && (
            <Select
              label={t('voice.suggestion.whichProduct')}
              value={p.scheduleId ?? ''}
              disabled={disabled}
              onFocus={focus}
              onChange={(e) => {
                const candidate = p.candidates.find((c) => c.scheduleId === e.target.value);
                if (candidate) {
                  change<'dosing_change'>({
                    ...p,
                    scheduleId: candidate.scheduleId,
                    productName: candidate.productName,
                    unit: candidate.unit,
                    currentAmount: candidate.currentAmount,
                  });
                }
              }}
              options={[
                { value: '', label: t('voice.suggestion.chooseProduct'), disabled: true },
                ...p.candidates.map((c) => ({ value: c.scheduleId, label: `${c.productName} (${c.unit})` })),
              ]}
            />
          )}
          {showEditors && editing && (
            <DecimalEditor
              label={t('voice.suggestion.newDose')}
              value={p.newAmount}
              unit={p.unit}
              disabled={disabled}
              onFocus={focus}
              onValue={(newAmount) => change<'dosing_change'>({ ...p, newAmount })}
            />
          )}
          {showEditors && editToggle}
        </>
      );
      break;
    }
    case 'restock': {
      const p = payload as SuggestionPayloadByKind['restock'];
      details = (
        <>
          <Text variant="caption" color="muted">
            {p.dueDate ? t('voice.suggestion.dueOn', { date: formatDay(p.dueDate, language) }) : t('voice.suggestion.noDueDate')}
            {' · '}
            {t('voice.suggestion.restockAsTask', { title: p.title })}
          </Text>
          {showEditors && editing && (
            <div className="suggestion-row__editors">
              <Input
                label={t('voice.suggestion.taskTitle')}
                value={p.title}
                maxLength={200}
                disabled={disabled}
                onFocus={focus}
                onChange={(e) => change<'restock'>({ ...p, title: e.target.value })}
              />
              <Input
                type="date"
                label={t('voice.suggestion.dueDate')}
                value={p.dueDate ?? ''}
                disabled={disabled}
                onFocus={focus}
                onChange={(e) => change<'restock'>({ ...p, dueDate: e.target.value || null })}
              />
            </div>
          )}
          {showEditors && editToggle}
        </>
      );
      break;
    }
    case 'water_change': {
      const p = payload as SuggestionPayloadByKind['water_change'];
      details = showEditors ? (
        <>
          {editing && (
            <DecimalEditor
              label={t('voice.suggestion.volume')}
              value={p.volumeLiters}
              unit="L"
              disabled={disabled}
              onFocus={focus}
              onValue={(volumeLiters) => change<'water_change'>({ ...p, volumeLiters })}
            />
          )}
          {editToggle}
        </>
      ) : null;
      break;
    }
    case 'maintenance': {
      const p = payload as SuggestionPayloadByKind['maintenance'];
      details = showEditors ? (
        <>
          {editing && (
            <Input
              label={t('voice.suggestion.note')}
              value={p.note}
              maxLength={500}
              disabled={disabled}
              onFocus={focus}
              onChange={(e) => change<'maintenance'>({ ...p, note: e.target.value })}
            />
          )}
          {editToggle}
        </>
      ) : null;
      break;
    }
    case 'task_complete': {
      const p = payload as SuggestionPayloadByKind['task_complete'];
      const choose = p.taskRef === null || p.candidates.length > 1;
      details =
        showEditors && choose && p.candidates.length > 0 ? (
          <Select
            label={t('voice.suggestion.whichTask')}
            value={p.taskRef ?? ''}
            disabled={disabled}
            onFocus={focus}
            onChange={(e) => change<'task_complete'>({ ...p, taskRef: e.target.value || null })}
            options={[
              { value: '', label: t('voice.suggestion.chooseTask'), disabled: true },
              ...p.candidates.map((c) => ({
                value: c.ref,
                label: c.dueDate ? `${c.title} · ${formatDay(c.dueDate, language)}` : c.title,
              })),
            ]}
          />
        ) : null;
      break;
    }
    case 'task_create': {
      const p = payload as SuggestionPayloadByKind['task_create'];
      details = (
        <>
          <Text variant="caption" color="muted">
            {p.dueDate ? t('voice.suggestion.dueOn', { date: formatDay(p.dueDate, language) }) : t('voice.suggestion.noDueDate')}
          </Text>
          {showEditors && editing && (
            <div className="suggestion-row__editors">
              <Input
                label={t('voice.suggestion.taskTitle')}
                value={p.title}
                maxLength={200}
                disabled={disabled}
                onFocus={focus}
                onChange={(e) => change<'task_create'>({ ...p, title: e.target.value })}
              />
              <Input
                type="date"
                label={t('voice.suggestion.dueDate')}
                value={p.dueDate ?? ''}
                disabled={disabled}
                onFocus={focus}
                onChange={(e) => change<'task_create'>({ ...p, dueDate: e.target.value || null })}
              />
            </div>
          )}
          {showEditors && editToggle}
        </>
      );
      break;
    }
    case 'task_recurring': {
      const p = payload as SuggestionPayloadByKind['task_recurring'];
      details = (
        <>
          <Text variant="caption" color="muted">
            {recurrenceSummary(p.recurrence, t, language)} · {t('voice.suggestion.startsOn', { date: formatDay(p.startDate, language) })}
          </Text>
          {showEditors && editing && (
            <div className="suggestion-row__editors">
              <Input
                label={t('voice.suggestion.taskTitle')}
                value={p.title}
                maxLength={200}
                disabled={disabled}
                onFocus={focus}
                onChange={(e) => change<'task_recurring'>({ ...p, title: e.target.value })}
              />
              <Input
                type="date"
                label={t('voice.suggestion.startDate')}
                value={p.startDate}
                required
                disabled={disabled}
                onFocus={focus}
                onChange={(e) => {
                  if (e.target.value) change<'task_recurring'>({ ...p, startDate: e.target.value });
                }}
              />
            </div>
          )}
          {showEditors && editToggle}
        </>
      );
      break;
    }
    case 'livestock_added': {
      const p = payload as SuggestionPayloadByKind['livestock_added'];
      details = (
        <>
          <Text variant="caption" color="muted">{t(`livestock.categories.${p.category}`)}</Text>
          {p.category === 'fish' && (
            <Text variant="caption">
              {t('voice.suggestion.quarantineHint')}{' '}
              {aquariumId && (
                <Link className="suggestion-row__link" to={`/aquariums/${encodeURIComponent(aquariumId)}/quarantine`}>
                  {t('voice.suggestion.quarantineLink')}
                </Link>
              )}
            </Text>
          )}
          {showEditors && editing && (
            <div className="suggestion-row__editors">
              <Input
                label={t('voice.suggestion.animalName')}
                value={p.commonName}
                maxLength={100}
                disabled={disabled}
                onFocus={focus}
                onChange={(e) => change<'livestock_added'>({ ...p, commonName: e.target.value })}
              />
              <Select
                label={t('voice.suggestion.animalCategory')}
                value={p.category}
                disabled={disabled}
                onFocus={focus}
                onChange={(e) => change<'livestock_added'>({ ...p, category: e.target.value as typeof p.category })}
                options={LIVESTOCK_CATEGORIES_FOR_SUGGESTIONS.map((c) => ({ value: c, label: String(t(`livestock.categories.${c}`)) }))}
              />
            </div>
          )}
          {showEditors && editToggle}
        </>
      );
      break;
    }
    case 'livestock_lost': {
      const p = payload as SuggestionPayloadByKind['livestock_lost'];
      const choose = p.livestockId === null || p.candidates.length > 1;
      details = (
        <>
          <Text variant="caption" color="muted">{t('voice.suggestion.deathOpensIncident')}</Text>
          {showEditors && choose && p.candidates.length > 0 && (
            <Select
              label={t('voice.suggestion.whichAnimal')}
              value={p.livestockId ?? ''}
              disabled={disabled}
              onFocus={focus}
              onChange={(e) => {
                const candidate = p.candidates.find((c) => c.livestockId === e.target.value);
                if (candidate) change<'livestock_lost'>({ ...p, livestockId: candidate.livestockId, commonName: candidate.commonName });
              }}
              options={[
                { value: '', label: t('voice.suggestion.chooseAnimal'), disabled: true },
                ...p.candidates.map((c) => ({ value: c.livestockId, label: c.commonName })),
              ]}
            />
          )}
        </>
      );
      break;
    }
    case 'incident': {
      const p = payload as SuggestionPayloadByKind['incident'];
      details = showEditors ? (
        <>
          {editing && (
            <Input
              label={t('voice.suggestion.note')}
              value={p.note}
              maxLength={500}
              disabled={disabled}
              onFocus={focus}
              onChange={(e) => change<'incident'>({ ...p, note: e.target.value })}
            />
          )}
          {editToggle}
        </>
      ) : null;
      break;
    }
    case 'expense': {
      const p = payload as SuggestionPayloadByKind['expense'];
      details = (
        <>
          <Text variant="caption" color="muted">
            {[p.note, formatDay(p.spentOn, language)].filter(Boolean).join(' · ')}
          </Text>
          {showEditors && editing && (
            <div className="suggestion-row__editors">
              <DecimalEditor
                label={t('voice.suggestion.amount')}
                value={p.amount}
                unit={p.currency}
                disabled={disabled}
                onFocus={focus}
                onValue={(amount) => change<'expense'>({ ...p, amount })}
              />
              <Select
                label={t('voice.suggestion.expenseCategory')}
                value={p.category}
                disabled={disabled}
                onFocus={focus}
                onChange={(e) => change<'expense'>({ ...p, category: e.target.value as typeof p.category })}
                options={BUDGET_CATEGORIES_FOR_SUGGESTIONS.map((c) => ({ value: c, label: String(t(`budget.categories.${c}`)) }))}
              />
              <Input
                label={t('voice.suggestion.note')}
                value={p.note ?? ''}
                maxLength={200}
                disabled={disabled}
                onFocus={focus}
                onChange={(e) => change<'expense'>({ ...p, note: e.target.value || null })}
              />
            </div>
          )}
          {showEditors && editToggle}
        </>
      );
      break;
    }
    default:
      details = null;
  }

  return (
    <div
      className={`suggestion-row${suggestion.needsReview ? ' suggestion-row--check' : ''}`}
      data-testid={`suggestion-row-${suggestion.id}`}
    >
      <div className="suggestion-row__head">
        <Checkbox
          label={text}
          checked={checked}
          disabled={disabled}
          className="suggestion-row__checkbox"
          aria-describedby={reasons.length > 0 ? reasonId : undefined}
          onFocus={focus}
          onChange={(e) => onCheckedChange(e.target.checked)}
        />
        <span className="suggestion-row__badges">
          {showKind && <Badge tone="info">{kindLabel(kind, t)}</Badge>}
          {suggestion.needsReview && <Badge tone="warning">{t('voice.suggestion.lowConfidence')}</Badge>}
        </span>
      </div>
      {(reasons.length > 0 || details !== null || (showPhrase && suggestion.phrase)) && (
        <div className="suggestion-row__body">
          {reasons.length > 0 && (
            <Text variant="caption" id={reasonId} className="suggestion-row__reason">
              {reasons.map((reason) => t(reasonKey(kind, reason))).join(' ')}
            </Text>
          )}
          {showPhrase && suggestion.phrase && (
            <Text variant="caption" color="muted">{t('voice.suggestion.fromPhrase', { phrase: suggestion.phrase })}</Text>
          )}
          {details}
        </div>
      )}
    </div>
  );
}
